// File: src/components/layout/Footer.js
/**
 * Universal Footer Component
 * Displays on all pages with company contact info and links
 */

import React from 'react';
import { Phone, Mail, MapPin } from 'lucide-react';
import { useLanguage } from '../../i18n/LanguageContext';
import '../../styles/components/footer.css';

const Footer = () => {
    const { t } = useLanguage();
    const currentYear = new Date().getFullYear();

    const handlePhoneClick = () => {
        window.location.href = 'tel:8444237475';
    };

    const handleEmailClick = () => {
        window.location.href = 'mailto:info@cdrisk.com';
    };

    const handleAddressClick = () => {
        window.open('https://maps.google.com/?q=299+S.+Main+St.,+Ste.+1300,+Salt+Lake+City,+UT+84111', '_blank');
    };

    const handleWebsiteClick = () => {
        window.open('https://cdrisk.com', '_blank', 'noopener,noreferrer');
    };

    return (
        <footer className="app-footer">
            <div className="footer-container">
                {/* Company Info */}
                <div className="footer-section footer-brand">
                    <h3 className="footer-title">Critical Dynamics Risk Management</h3>
                    <p className="footer-description">
                        {t('footer.description')}
                    </p>
                </div>

                {/* Contact Information */}
                <div className="footer-section footer-contact">
                    <h4 className="footer-heading">{t('footer.contact')}</h4>
                    <ul className="footer-links">
                        <li>
                            <button
                                className="footer-link"
                                onClick={handlePhoneClick}
                                aria-label="Call us"
                            >
                                <Phone size={16} />
                                <span>(844) 4-CDRISK</span>
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={handleEmailClick}
                                aria-label="Email us"
                            >
                                <Mail size={16} />
                                <span>info@cdrisk.com</span>
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={handleAddressClick}
                                aria-label="View our location"
                            >
                                <MapPin size={16} />
                                <span>299 S. Main St., Ste. 1300<br />Salt Lake City, UT 84111</span>
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Quick Links */}
                <div className="footer-section footer-links-section">
                    <h4 className="footer-heading">{t('footer.quickLinks')}</h4>
                    <ul className="footer-links">
                        <li>
                            <button
                                className="footer-link"
                                onClick={handleWebsiteClick}
                            >
                                CDRisk.com
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={() => window.location.href = '/'}
                            >
                                {t('footer.oshaRegs')}
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={() => window.open('https://www.cdrisk.com/portal', '_blank', 'noopener,noreferrer')}
                            >
                                {t('footer.clientPortal')}
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={() => window.open('https://www.cdrisk.com/contact', '_blank', 'noopener,noreferrer')}
                            >
                                {t('footer.consultation')}
                            </button>
                        </li>
                    </ul>
                </div>

                {/* Resources */}
                <div className="footer-section footer-resources">
                    <h4 className="footer-heading">{t('footer.resources')}</h4>
                    <ul className="footer-links">
                        <li>
                            <button
                                className="footer-link"
                                onClick={() => window.location.href = '/?reg=1910'}
                            >
                                29 CFR 1910
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={() => window.location.href = '/?reg=1926'}
                            >
                                29 CFR 1926
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={() => window.open('https://sds.guide', '_blank', 'noopener,noreferrer')}
                            >
                                {t('footer.sdsGuide')}
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={() => window.open('https://www.cdrisk.com/services/fire-safety-audits', '_blank', 'noopener,noreferrer')}
                            >
                                {t('footer.fireSafety')}
                            </button>
                        </li>
                        <li>
                            <button
                                className="footer-link"
                                onClick={() => window.open('https://www.cdrisk.com/services/osha-compliance', '_blank', 'noopener,noreferrer')}
                            >
                                {t('footer.compliance')}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>

            {/* Copyright Bar */}
            <div className="footer-bottom">
                <div className="footer-container">
                    <p className="footer-copyright">
                        © {currentYear} Critical Dynamics Risk Management. {t('footer.rights')}
                    </p>
                    {/* Real links to the real documents. These were buttons
                        that opened cdrisk.com's homepage — a "Privacy Policy"
                        control that does not lead to the privacy policy is
                        worse than no control, because it looks like the
                        document was provided. Anchors rather than buttons so
                        they can be opened in a tab, copied, and read by a
                        screen reader as the links they are. */}
                    <div className="footer-legal">
                        <a
                            className="footer-legal-link"
                            href="https://cdrisk.com/privacy-policy"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('footer.privacy')}
                        </a>
                        <span className="footer-separator">•</span>
                        <a
                            className="footer-legal-link"
                            href="https://cdrisk.com/terms-conditions"
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {t('footer.terms')}
                        </a>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
